import { Button } from "@volca/ui/components/ui/button";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { textVariants } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { SectionHeading } from "@/accounts-payable/common";
import type { ApVendor, ApVendorUpdate } from "@/accounts-payable/types";
import type { QuickbooksAccount } from "@/business/quickbooks";
import { quickbooksAccountLabel } from "@/business/quickbooks-utils";
import { QuickbooksExpenseAccountSelect } from "./quickbooks-expense-account-select";
import {
  QUICKBOOKS_EXPENSE_ACCOUNT_LABEL,
  QUICKBOOKS_NOT_LINKED,
  QUICKBOOKS_VENDOR_LABEL,
} from "./quickbooks-labels";
import { QuickbooksVendorDialog } from "./quickbooks-vendor-dialog";

const ACCOUNT_FIELD_ID = "ap-vendor-quickbooks-expense-account";

/**
 * Where this vendor's bills go in QuickBooks: the linked QuickBooks vendor and
 * an optional expense account override. Labels match the Vendors table.
 */
export const QuickbooksSection = ({
  vendor,
  businessId,
  defaultAccount,
  isSaving,
  onUpdate,
}: {
  vendor: ApVendor;
  businessId: string | null;
  /** The business default, named so "Business default" says what it means. */
  defaultAccount: QuickbooksAccount | null;
  isSaving: boolean;
  onUpdate: (changes: ApVendorUpdate, onSuccess?: () => void) => void;
}) => {
  const linked = vendor.quickbooks_vendor;

  // Unlinking saves at once, so it offers Undo like removing an alias does.
  const unlink = () => {
    if (!linked) return;
    onUpdate({ quickbooks_vendor_id: null }, () =>
      toast(`Unlinked from ${linked.display_name}`, {
        action: {
          label: "Undo",
          onClick: () => onUpdate({ quickbooks_vendor_id: linked.id }),
        },
      }),
    );
  };

  return (
    <Stack className="w-full" align="start" gap={4}>
      <SectionHeading>QuickBooks</SectionHeading>

      <Stack className="w-full" align="start" gap={2}>
        <Text variant="label" tone="muted">
          {QUICKBOOKS_VENDOR_LABEL}
        </Text>
        {linked ? (
          <Text>{linked.display_name}</Text>
        ) : (
          <Text tone="muted">
            {QUICKBOOKS_NOT_LINKED}. Each bill for this vendor asks which
            QuickBooks vendor to use until you link one.
          </Text>
        )}
        <Stack direction="row" gap={4} align="center">
          <QuickbooksVendorDialog
            businessId={businessId}
            currentVendorId={linked?.id ?? null}
            triggerLabel={linked ? "Change" : "Link"}
            triggerAriaLabel={`${linked ? "Change" : "Link"} QuickBooks vendor for ${vendor.name}`}
            title={`Link ${vendor.name} to QuickBooks`}
            description="Choose the QuickBooks vendor for this vendor's bills"
            isSaving={isSaving}
            onLink={(quickbooksVendor, done) =>
              onUpdate({ quickbooks_vendor_id: quickbooksVendor.id }, done)
            }
          />
          {linked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={unlink}
              disabled={isSaving}
              aria-label={`Unlink ${vendor.name} from ${linked.display_name}`}
            >
              Unlink
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack className="w-full" align="start" gap={2}>
        <Label
          htmlFor={ACCOUNT_FIELD_ID}
          className={cn(textVariants({ variant: "label" }), "text-subtle")}
        >
          {QUICKBOOKS_EXPENSE_ACCOUNT_LABEL}
        </Label>
        <QuickbooksExpenseAccountSelect
          id={ACCOUNT_FIELD_ID}
          businessId={businessId}
          value={vendor.quickbooks_expense_account}
          disabled={isSaving}
          onChange={(accountId) =>
            onUpdate({ quickbooks_expense_account_id: accountId })
          }
        />
        <Text variant="caption" tone="muted">
          {defaultAccount
            ? `The business default is ${quickbooksAccountLabel(defaultAccount)}.`
            : "No business default is set yet. Choose one in QuickBooks bill settings."}
        </Text>
      </Stack>
    </Stack>
  );
};
